import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, TrendingUp, TrendingDown, Minus, X, Zap, AlertTriangle, Loader2 } from 'lucide-react';
import { getTheme } from '../App';
import { useWS } from '../WSContext';
import { useToast } from '../components/Toast';

// ─── API endpoints ─────────────────────────────────────────────────────────────
const DYDX_API = 'https://indexer.dydx.trade/v4';
const BF_PROXY = window.location.port === '3000' || window.location.port === '3001'
  ? `http://${window.location.hostname}:8890`
  : '/bf-data'; // relative — always same-origin

// ─── Fees (standard tier) ──────────────────────────────────────────────────────
const DYDX_MAKER = 0.0002; // 0.02%
const DYDX_TAKER = 0.0005; // 0.05%

// ─── Symbol mapping ────────────────────────────────────────────────────────────
const BF_TO_DYDX: Record<string, string> = {
  'BTC':  'BTC-USD',
  'ETH':  'ETH-USD',
  'SOL':  'SOL-USD',
  'SUI':  'SUI-USD',
  'HYPE': 'HYPE-USD',
  'GOLD': 'PAXG-USD', // Bluefin GOLD-PERP ↔ dYdX PAXG (altına 1:1 endeksli)
};

// ─── Types ─────────────────────────────────────────────────────────────────────
interface BfTicker {
  symbol: string;
  bestBidPriceE9: string;
  bestAskPriceE9: string;
  lastFundingRateE9: string;
  estimatedFundingRateE9: string;
  nextFundingTimeAtMillis: number;
  oraclePriceE9: string;
}
interface BfMarketInfo {
  symbol: string;
  defaultMakerFeeE9: string;
  defaultTakerFeeE9: string;
  initialMarginRatioE9: string;
}
interface DydxMarket {
  ticker: string;
  nextFundingRate: string;
  oraclePrice: string;
  initialMarginFraction: string;
}
interface DydxOrderbook {
  bids: { price: string; size: string }[];
  asks: { price: string; size: string }[];
}
interface Row {
  base: string;
  bfSymbol: string;
  dydxSymbol: string;
  bfBid: number;
  bfAsk: number;
  bfMidSpreadPct: number;
  bfFunding: number;
  bfFundingEst: number;
  bfMaker: number;
  bfTaker: number;
  dydxBid: number;
  dydxAsk: number;
  dydxMidSpreadPct: number;
  dydxFunding: number;
  netShortDydxLongBf: number;
  netLongDydxShortBf: number;
  bestRate: number;
  bestDir: 'S_DYDX_L_BF' | 'L_DYDX_S_BF' | 'NONE';
  dydxOracle: number;
  bfOracle: number;
  dydxMaxLev: number;
  bfMaxLev: number;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
const e9  = (s: string | number) => Number(s) / 1e9;
const pct = (n: number, d = 4) => `${(n * 100).toFixed(d)}%`;
const fmt = (n: number) =>
  n >= 1000 ? n.toLocaleString('en-US', { maximumFractionDigits: 2 })
  : n >= 1  ? n.toFixed(4)
  : n.toFixed(6);

// ─── Sub-components ────────────────────────────────────────────────────────────
function FeeCard({ label, maker, taker, dark }: { label: string; maker: number; taker: number; dark: boolean }) {
  const t = getTheme(dark);
  return (
    <div className={`rounded-xl border ${t.cardBorder} ${t.card} p-4`}>
      <p className="text-[11px] font-bold uppercase tracking-widest text-blue-500 mb-2">{label}</p>
      <div className="flex gap-4">
        <div>
          <p className="text-[10px] text-blue-500">Maker</p>
          <p className="text-sm font-bold text-emerald-400">{pct(maker, 3)}</p>
        </div>
        <div>
          <p className="text-[10px] text-blue-500">Taker</p>
          <p className="text-sm font-bold text-rose-400">{pct(taker, 3)}</p>
        </div>
      </div>
    </div>
  );
}

function FundingBadge({ rate }: { rate: number }) {
  const color = rate > 0.00001 ? 'text-emerald-400' : rate < -0.00001 ? 'text-rose-400' : 'text-blue-400';
  const icon  = rate > 0.00001 ? <TrendingUp size={10} /> : rate < -0.00001 ? <TrendingDown size={10} /> : <Minus size={10} />;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono text-xs ${color}`}>
      {icon}{pct(rate, 5)}
    </span>
  );
}

// ─── Order Modal ───────────────────────────────────────────────────────────────
type SizeUnit = 'USDC' | 'TOKEN';

interface ModalState {
  dydxDir:   'LONG' | 'SHORT';
  bfDir:     'LONG' | 'SHORT';
  dydxPrice: string;
  bfPrice:   string;
  sizeRaw:   string;
  sizeUnit:  SizeUnit;
  dydxLev:   number;
  bfLev:     number;
}

const LEVERAGES = [1, 2, 3, 5, 10, 20];

function DirToggle({
  value, onChange, exchange,
}: { value: 'LONG' | 'SHORT'; onChange: (d: 'LONG' | 'SHORT') => void; exchange: 'dydx' | 'bf' }) {
  const accent = exchange === 'dydx' ? 'border-blue-500/60' : 'border-cyan-500/60';
  return (
    <div className={`flex rounded-lg border overflow-hidden text-xs font-black ${accent}`}>
      <button
        onClick={() => onChange('LONG')}
        className={`flex-1 py-1.5 transition-colors ${
          value === 'LONG'
            ? 'bg-emerald-500/30 text-emerald-300'
            : 'bg-[#061530] text-blue-600 hover:text-emerald-500'
        }`}
      >LONG</button>
      <button
        onClick={() => onChange('SHORT')}
        className={`flex-1 py-1.5 border-l border-[#0a2560] transition-colors ${
          value === 'SHORT'
            ? 'bg-rose-500/30 text-rose-300'
            : 'bg-[#061530] text-blue-600 hover:text-rose-500'
        }`}
      >SHORT</button>
    </div>
  );
}

function LevPicker({ value, onChange }: { value: number; onChange: (l: number) => void }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {LEVERAGES.map(lev => (
        <button
          key={lev}
          onClick={() => onChange(lev)}
          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition-colors ${
            value === lev
              ? 'bg-[#4A9FE8]/20 border-[#4A9FE8] text-[#4A9FE8]'
              : 'bg-[#061530] border-[#0a2560] text-blue-500 hover:border-blue-500 hover:text-blue-300'
          }`}
        >{lev}×</button>
      ))}
    </div>
  );
}

function OrderModal({ row, dark, onClose, sendWs, onRecordOpen }: {
  row: Row; dark: boolean; onClose: () => void;
  sendWs: (msg: object) => void;
  onRecordOpen?: (r: ArbRecord) => void;
}) {
  const t = getTheme(dark);
  const { showToast } = useToast();
  const [sending,   setSending]   = useState(false);
  const [postOnly,  setPostOnly]  = useState(false);

  const defaultDydxDir: 'LONG' | 'SHORT' = row.bestDir === 'S_DYDX_L_BF' ? 'SHORT' : 'LONG';
  const defaultBfDir:   'LONG' | 'SHORT' = row.bestDir === 'S_DYDX_L_BF' ? 'LONG'  : 'SHORT';

  const priceStr = (p: number) => p.toFixed(p >= 100 ? 2 : 4);

  const [modal, setModal] = useState<ModalState>({
    dydxDir:   defaultDydxDir,
    bfDir:     defaultBfDir,
    dydxPrice: priceStr(defaultDydxDir === 'LONG' ? row.dydxAsk : row.dydxBid),
    bfPrice:   priceStr(defaultBfDir   === 'LONG' ? row.bfAsk   : row.bfBid),
    sizeRaw:   '1000',
    sizeUnit:  'USDC',
    dydxLev:   5,
    bfLev:     20,
  });

  // When direction changes, update the default price to match new side
  const setDydxDir = (d: 'LONG' | 'SHORT') => setModal(m => ({
    ...m, dydxDir: d,
    dydxPrice: priceStr(d === 'LONG' ? row.dydxAsk : row.dydxBid),
  }));
  const setBfDir = (d: 'LONG' | 'SHORT') => setModal(m => ({
    ...m, bfDir: d,
    bfPrice: priceStr(d === 'LONG' ? row.bfAsk : row.bfBid),
  }));

  const dirColor = (d: string) => d === 'LONG' ? 'text-emerald-400' : 'text-rose-400';
  const dirBg    = (d: string) => d === 'LONG'
    ? 'bg-emerald-500/10 border-emerald-500/30'
    : 'bg-rose-500/10 border-rose-500/30';

  const midPrice = (row.dydxBid + row.dydxAsk) / 2 || 1;
  const rawVal   = parseFloat(modal.sizeRaw) || 0;
  const sizeUsd  = modal.sizeUnit === 'USDC' ? rawVal : rawVal * midPrice;
  const tokenAmt = modal.sizeUnit === 'TOKEN' ? rawVal : rawVal / midPrice;
  const tokenDec = midPrice > 1000 ? 6 : midPrice > 1 ? 4 : 2;

  const toggleUnit = () => setModal(m => {
    const cur = parseFloat(m.sizeRaw) || 0;
    return m.sizeUnit === 'USDC'
      ? { ...m, sizeUnit: 'TOKEN', sizeRaw: (midPrice > 0 ? cur / midPrice : 0).toFixed(tokenDec) }
      : { ...m, sizeUnit: 'USDC',  sizeRaw: (cur * midPrice).toFixed(2) };
  });

  const entryFeePct  = DYDX_MAKER + row.bfMaker;
  const exitFeePct   = DYDX_MAKER + row.bfMaker;
  const roundTrip    = entryFeePct + exitFeePct;
  const earnPerHour  = row.bestRate * sizeUsd;
  const breakEvenH   = row.bestRate > 0 ? roundTrip / row.bestRate : Infinity;
  const breakEvenMin = isFinite(breakEvenH) ? breakEvenH * 60 : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3">
      <div className={`w-full max-w-2xl rounded-2xl border ${t.cardBorder} bg-[#030d1f] shadow-2xl`}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#0a2560]">
          <div className="flex items-center gap-2">
            <span className="text-base font-black text-[#4A9FE8]">{row.base}</span>
            <span className="text-[11px] text-blue-500">funding arb</span>
            {row.bestRate > 0 && (
              <span className="text-[11px] font-mono text-emerald-400 bg-emerald-900/30 px-2 py-0.5 rounded-full border border-emerald-700/40">
                +{pct(row.bestRate, 4)}/h
              </span>
            )}
            <span className="text-[11px] text-blue-600 ml-1">{modal.dydxDir} dYdX · {modal.bfDir} BF</span>
          </div>
          <button onClick={onClose} className="text-blue-500 hover:text-white transition-colors p-1">
            <X size={16} />
          </button>
        </div>

        <div className="p-3 space-y-2.5">

          {/* ── Two exchange columns ── */}
          <div className="grid grid-cols-2 gap-2.5">

            {/* dYdX */}
            <div className={`rounded-xl border p-3 space-y-2 ${dirBg(modal.dydxDir)}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-blue-300 uppercase tracking-wider">dYdX</span>
                <span className="text-[10px] text-blue-600">Post-Only · Maker {pct(DYDX_MAKER, 3)}</span>
              </div>

              <DirToggle value={modal.dydxDir} onChange={setDydxDir} exchange="dydx" />

              {/* Price + bid/ask buttons inline */}
              <div>
                <input
                  type="number"
                  value={modal.dydxPrice}
                  onChange={e => setModal(m => ({ ...m, dydxPrice: e.target.value }))}
                  className="w-full bg-[#061530] border border-[#0a2560] rounded-lg px-2.5 py-1.5 text-xs font-mono text-white focus:border-[#4A9FE8] outline-none"
                  placeholder="Limit fiyat"
                />
                <div className="flex gap-1 mt-1">
                  <button
                    onClick={() => setModal(m => ({ ...m, dydxPrice: priceStr(row.dydxBid) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${
                      modal.dydxPrice === priceStr(row.dydxBid)
                        ? 'bg-emerald-500/20 border-emerald-500/60 text-emerald-300'
                        : 'bg-[#061530] border-[#0a2560] text-blue-500 hover:border-emerald-600 hover:text-emerald-400'
                    }`}
                  >Bid {fmt(row.dydxBid)}</button>
                  <button
                    onClick={() => setModal(m => ({ ...m, dydxPrice: priceStr((row.dydxBid + row.dydxAsk) / 2) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${
                      modal.dydxPrice === priceStr((row.dydxBid + row.dydxAsk) / 2)
                        ? 'bg-[#4A9FE8]/20 border-[#4A9FE8]/60 text-[#4A9FE8]'
                        : 'bg-[#061530] border-[#0a2560] text-blue-500 hover:border-[#4A9FE8]/60 hover:text-[#4A9FE8]'
                    }`}
                  >Mid {fmt((row.dydxBid + row.dydxAsk) / 2)}</button>
                  <button
                    onClick={() => setModal(m => ({ ...m, dydxPrice: priceStr(row.dydxAsk) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${
                      modal.dydxPrice === priceStr(row.dydxAsk)
                        ? 'bg-rose-500/20 border-rose-500/60 text-rose-300'
                        : 'bg-[#061530] border-[#0a2560] text-blue-500 hover:border-rose-600 hover:text-rose-400'
                    }`}
                  >Ask {fmt(row.dydxAsk)}</button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] text-blue-500">Kaldıraç</p>
                  {row.dydxMaxLev > 0 && <span className="text-[9px] text-blue-600">Max {row.dydxMaxLev}×</span>}
                </div>
                <LevPicker value={modal.dydxLev} onChange={lev => setModal(m => ({ ...m, dydxLev: lev }))} />
              </div>

              <div className="flex items-center justify-between text-[10px]">
                <span className="text-blue-500">Funding 1h</span>
                <FundingBadge rate={row.dydxFunding} />
              </div>
            </div>

            {/* Bluefin */}
            <div className={`rounded-xl border p-3 space-y-2 ${dirBg(modal.bfDir)}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-cyan-400 uppercase tracking-wider">Bluefin</span>
                <span className="text-[10px] text-blue-600">Post-Only · Maker {pct(row.bfMaker || 0.00005, 3)}</span>
              </div>

              <DirToggle value={modal.bfDir} onChange={setBfDir} exchange="bf" />

              {/* Price + bid/ask buttons inline */}
              <div>
                <input
                  type="number"
                  value={modal.bfPrice}
                  onChange={e => setModal(m => ({ ...m, bfPrice: e.target.value }))}
                  className="w-full bg-[#061530] border border-[#0a2560] rounded-lg px-2.5 py-1.5 text-xs font-mono text-white focus:border-[#4A9FE8] outline-none"
                  placeholder="Limit fiyat"
                />
                <div className="flex gap-1 mt-1">
                  <button
                    onClick={() => setModal(m => ({ ...m, bfPrice: priceStr(row.bfBid) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${
                      modal.bfPrice === priceStr(row.bfBid)
                        ? 'bg-emerald-500/20 border-emerald-500/60 text-emerald-300'
                        : 'bg-[#061530] border-[#0a2560] text-cyan-700 hover:border-emerald-600 hover:text-emerald-400'
                    }`}
                  >Bid {fmt(row.bfBid)}</button>
                  <button
                    onClick={() => setModal(m => ({ ...m, bfPrice: priceStr((row.bfBid + row.bfAsk) / 2) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${
                      modal.bfPrice === priceStr((row.bfBid + row.bfAsk) / 2)
                        ? 'bg-[#4A9FE8]/20 border-[#4A9FE8]/60 text-[#4A9FE8]'
                        : 'bg-[#061530] border-[#0a2560] text-cyan-700 hover:border-[#4A9FE8]/60 hover:text-[#4A9FE8]'
                    }`}
                  >Mid {fmt((row.bfBid + row.bfAsk) / 2)}</button>
                  <button
                    onClick={() => setModal(m => ({ ...m, bfPrice: priceStr(row.bfAsk) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${
                      modal.bfPrice === priceStr(row.bfAsk)
                        ? 'bg-rose-500/20 border-rose-500/60 text-rose-300'
                        : 'bg-[#061530] border-[#0a2560] text-cyan-700 hover:border-rose-600 hover:text-rose-400'
                    }`}
                  >Ask {fmt(row.bfAsk)}</button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] text-blue-500">Kaldıraç</p>
                  {row.bfMaxLev > 0 && <span className="text-[9px] text-blue-600">Max {row.bfMaxLev}×</span>}
                </div>
                <LevPicker value={modal.bfLev} onChange={lev => setModal(m => ({ ...m, bfLev: lev }))} />
              </div>

              <div className="flex items-center justify-between text-[10px]">
                <span className="text-blue-500">Funding 1h (est)</span>
                <FundingBadge rate={row.bfFundingEst} />
              </div>
            </div>
          </div>

          {/* ── Size ── */}
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={modal.sizeRaw}
                  onChange={e => setModal(m => ({ ...m, sizeRaw: e.target.value }))}
                  className="flex-1 w-full bg-[#061530] border border-[#0a2560] rounded-lg px-2.5 py-1.5 text-xs font-mono text-white focus:border-[#4A9FE8] outline-none"
                  placeholder={modal.sizeUnit === 'USDC' ? '1000' : '0.01'}
                />
                <button
                  onClick={toggleUnit}
                  className="flex-shrink-0 flex rounded-lg border border-[#0a2560] overflow-hidden text-[10px] font-bold"
                >
                  <span className={`px-2.5 py-1.5 transition-colors ${modal.sizeUnit === 'USDC' ? 'bg-[#4A9FE8]/20 text-[#4A9FE8]' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}>USDC</span>
                  <span className={`px-2.5 py-1.5 border-l border-[#0a2560] transition-colors ${modal.sizeUnit === 'TOKEN' ? 'bg-[#4A9FE8]/20 text-[#4A9FE8]' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}>{row.base}</span>
                </button>
              </div>
              <div className="flex justify-between mt-1 text-[10px] text-blue-600">
                {modal.sizeUnit === 'USDC'
                  ? <span>≈ <span className="font-mono text-blue-400">{tokenAmt.toFixed(tokenDec)} {row.base}</span></span>
                  : <span>≈ <span className="font-mono text-blue-400">${sizeUsd.toLocaleString('en-US', { maximumFractionDigits: 0 })} USDC</span></span>
                }
                <span>Toplam teminat: <span className="font-mono text-blue-400">${(sizeUsd * 2).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span></span>
              </div>
            </div>
          </div>

          {/* Summary — compact 4-col grid */}
          <div className="bg-[#061530]/80 rounded-xl border border-[#0a2560] px-3 py-2">
            <div className="grid grid-cols-2 gap-x-5 gap-y-1 text-[11px]">
              <div className="flex justify-between">
                <span className="text-blue-500">Round-trip fee</span>
                <span className="font-mono text-rose-400">−{pct(roundTrip, 4)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">Net / saat</span>
                <span className="font-mono text-emerald-400">+{pct(row.bestRate, 4)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">Break-even</span>
                <span className="font-mono text-[#4A9FE8]">{breakEvenMin !== null ? `~${breakEvenMin.toFixed(0)} dk` : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">Saatlik gelir</span>
                <span className="font-mono text-emerald-400">{sizeUsd > 0 ? `$${earnPerHour.toFixed(3)}` : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">dYdX notional ×{modal.dydxLev}</span>
                <span className="font-mono text-white">{sizeUsd > 0 ? `$${(sizeUsd * modal.dydxLev).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">BF notional ×{modal.bfLev}</span>
                <span className="font-mono text-white">{sizeUsd > 0 ? `$${(sizeUsd * modal.bfLev).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}</span>
              </div>
            </div>
          </div>

          {/* Maker / Taker toggle */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-blue-500">Emir tipi</span>
            <div className="flex rounded-lg border border-[#0a2560] overflow-hidden text-[10px] font-bold">
              <button
                onClick={() => setPostOnly(true)}
                className={`px-3 py-1.5 transition-colors ${postOnly ? 'bg-emerald-500/20 text-emerald-300' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}
              >Maker (Post-Only)</button>
              <button
                onClick={() => setPostOnly(false)}
                className={`px-3 py-1.5 border-l border-[#0a2560] transition-colors ${!postOnly ? 'bg-rose-500/20 text-rose-300' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}
              >Taker (Limit)</button>
            </div>
          </div>

          {/* Warning — one line */}
          <div className="flex items-center gap-1.5 text-[10px] text-yellow-600">
            <AlertTriangle size={11} className="flex-shrink-0" />
            <span>{postOnly ? 'Post-only emir fiyat değişiminde reddedilebilir.' : 'Taker emir piyasada dolar, yüksek fee uygulanır.'} Fonlama oranı periyotta değişebilir. Kısmi dolum riski.</span>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2.5">
            <button
              onClick={onClose}
              disabled={sending}
              className="flex-1 py-2 rounded-xl border border-[#0a2560] text-blue-400 text-xs font-semibold hover:bg-[#0a2560]/40 transition-colors disabled:opacity-40"
            >
              İptal
            </button>
            <button
              disabled={sending || !tokenAmt || tokenAmt <= 0}
              onClick={async () => {
                if (sending) return;
                setSending(true);
                try {
                  sendWs({
                    type: 'manual_arb_order',
                    payload: {
                      post_only: postOnly,
                      dydx: {
                        symbol:   row.dydxSymbol,
                        side:     modal.dydxDir.toLowerCase(),
                        price:    parseFloat(modal.dydxPrice),
                        quantity: parseFloat(tokenAmt.toFixed(8)),
                        leverage: modal.dydxLev,
                      },
                      bf: {
                        symbol:   row.bfSymbol,
                        side:     modal.bfDir.toLowerCase(),
                        price:    parseFloat(modal.bfPrice),
                        quantity: parseFloat(tokenAmt.toFixed(8)),
                        leverage: modal.bfLev,
                      },
                    },
                  });
                  // Yanıt WSContext'te arb_order_result ile gelir, burada sadece gönderimi bekle
                  await new Promise<void>((resolve) => {
                    const handler = (e: MessageEvent) => {
                      try {
                        const d = JSON.parse(e.data);
                        if (d.type === 'arb_order_result') {
                          window.removeEventListener('ws_message', handler as any);
                          if (d.payload?.success) {
                            showToast('⚡ Arb Emirleri Gönderildi', `dYdX ${modal.dydxDir} + BF ${modal.bfDir} — ${row.base}`, 'success');
                            onRecordOpen?.({
                              id: `${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
                              base: row.base,
                              dydxSymbol: row.dydxSymbol,
                              bfSymbol:   row.bfSymbol,
                              openTime:   Date.now(),
                              dydxDir:    modal.dydxDir.toLowerCase() as 'long' | 'short',
                              bfDir:      modal.bfDir.toLowerCase(),
                              qty:        parseFloat(tokenAmt.toFixed(8)),
                              dydxOpenPrice: parseFloat(modal.dydxPrice),
                              bfOpenPrice:   parseFloat(modal.bfPrice),
                              postOnly,
                              status: 'open',
                            });
                            onClose();
                          } else {
                            const dErr = d.payload?.dydx?.error;
                            const bErr = d.payload?.bf?.error;
                            showToast('Emir Hatası', [dErr, bErr].filter(Boolean).join(' | '), 'error');
                          }
                          resolve();
                        }
                      } catch {}
                    };
                    window.addEventListener('ws_message', handler as any);
                    setTimeout(() => { window.removeEventListener('ws_message', handler as any); resolve(); }, 20000);
                  });
                } finally {
                  setSending(false);
                }
              }}
              className="flex-1 py-2 rounded-xl bg-[#4A9FE8]/10 border border-[#4A9FE8]/30 text-[#4A9FE8] text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-[#4A9FE8]/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sending
                ? <><Loader2 size={13} className="animate-spin" /> Gönderiliyor...</>
                : <><Zap size={13} /> Her İki Emri Gönder</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Arb Button (replaces ArbBadge) ──────────────────────────────────────────
function ArbButton({ row, onOpen }: { row: Row; onOpen: (r: Row) => void }) {
  if (row.bestDir === 'NONE') return <span className="text-blue-600 text-xs">—</span>;

  const hot   = row.bestRate > 0.0001;
  const label = row.bestDir === 'S_DYDX_L_BF' ? 'S dYdX / L BF' : 'L dYdX / S BF';

  return (
    <button
      onClick={() => onOpen(row)}
      className={`flex flex-col items-end gap-0.5 group transition-all ${hot ? 'opacity-100' : 'opacity-75 hover:opacity-100'}`}
    >
      <span className={`text-[11px] font-mono font-bold px-2 py-1 rounded-lg border transition-all ${
        hot
          ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 group-hover:bg-emerald-500/35 group-hover:border-emerald-400'
          : 'bg-[#0a2560]/60 text-blue-400 border-[#0a2560] group-hover:border-blue-500 group-hover:text-blue-300'
      }`}>
        {pct(row.bestRate, 4)}/h ↗
      </span>
      <span className="text-[9px] text-blue-600 group-hover:text-blue-400 transition-colors">{label}</span>
    </button>
  );
}

// ─── Performans types ─────────────────────────────────────────────────────────
interface DydxPos {
  market: string; side: 'LONG' | 'SHORT';
  size: string; entryPrice: string;
  unrealizedPnl: string; netFunding: string; createdAt: string;
}
interface CloseCard {
  base: string;
  dydxTkr: string;
  bfSymbol: string;
  dydxSide: 'LONG' | 'SHORT';
  bfSide: string;       // 'long' | 'short'
  dydxSize: number;
  bfSize: number;
  matchRow: Row | null;
}

function CloseModal({ card, dark, onClose, sendWs, onRecordClose }: {
  card: CloseCard; dark: boolean; onClose: () => void;
  sendWs: (msg: object) => void;
  onRecordClose?: (base: string, dydxDir: string, closeTime: number, dydxClosePrice: number, bfClosePrice: number) => void;
}) {
  const t = getTheme(dark);
  const { showToast } = useToast();
  const [sending,  setSending]  = useState(false);
  const [postOnly, setPostOnly] = useState(true);  // maker default

  const row = card.matchRow;
  const priceStr = (p: number) => p > 0 ? p.toFixed(p >= 100 ? 2 : 4) : '';

  // Close direction = opposite of current position
  const dydxCloseDir: 'LONG' | 'SHORT' = card.dydxSide === 'LONG' ? 'SHORT' : 'LONG';
  const bfCloseDir:   'LONG' | 'SHORT' = card.bfSide   === 'long' ? 'SHORT' : 'LONG';

  // Maker defaults: SELL close → ask, BUY close → bid
  const initDydxPrice = row
    ? priceStr(dydxCloseDir === 'SHORT' ? row.dydxAsk : row.dydxBid)
    : '';
  const initBfPrice = row
    ? priceStr(bfCloseDir   === 'SHORT' ? row.bfAsk   : row.bfBid)
    : '';

  const [dydxPrice, setDydxPrice] = useState(initDydxPrice);
  const [bfPrice,   setBfPrice]   = useState(initBfPrice);

  const dirBg = (d: 'LONG' | 'SHORT') =>
    d === 'LONG' ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/30';
  const dirLabel = (d: 'LONG' | 'SHORT') => d === 'LONG'
    ? <span className="font-bold text-emerald-400">BUY / LONG</span>
    : <span className="font-bold text-rose-400">SELL / SHORT</span>;

  const canSend = parseFloat(dydxPrice) > 0 && parseFloat(bfPrice) > 0
    && card.dydxSize > 0 && card.bfSize > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3">
      <div className={`w-full max-w-xl rounded-2xl border ${t.cardBorder} bg-[#030d1f] shadow-2xl`}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#0a2560]">
          <div className="flex items-center gap-2">
            <span className="text-base font-black text-[#4A9FE8]">{card.base}</span>
            <span className="text-[11px] text-rose-400 bg-rose-900/30 px-2 py-0.5 rounded-full border border-rose-700/40">Pozisyon Kapat</span>
            <span className="text-[10px] text-blue-500">dYdX {card.dydxSide} · BF {card.bfSide.toUpperCase()}</span>
          </div>
          <button onClick={onClose} className="text-blue-500 hover:text-white transition-colors p-1"><X size={16} /></button>
        </div>

        <div className="p-3 space-y-2.5">
          <div className="grid grid-cols-2 gap-2.5">

            {/* dYdX */}
            <div className={`rounded-xl border p-3 space-y-2 ${dirBg(dydxCloseDir)}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-blue-300 uppercase tracking-wider">dYdX</span>
                <span className="text-[10px] text-blue-600">{pct(DYDX_MAKER, 3)} maker</span>
              </div>
              <div className="text-[11px]">{dirLabel(dydxCloseDir)}</div>
              <div>
                <input
                  type="number" value={dydxPrice}
                  onChange={e => setDydxPrice(e.target.value)}
                  className="w-full bg-[#061530] border border-[#0a2560] rounded-lg px-2.5 py-1.5 text-xs font-mono text-white focus:border-[#4A9FE8] outline-none"
                  placeholder="Limit fiyat"
                />
                {row && (
                  <div className="flex gap-1 mt-1">
                    <button onClick={() => setDydxPrice(priceStr(row.dydxBid))}
                      className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${dydxPrice === priceStr(row.dydxBid) ? 'bg-emerald-500/20 border-emerald-500/60 text-emerald-300' : 'bg-[#061530] border-[#0a2560] text-blue-500 hover:border-emerald-600 hover:text-emerald-400'}`}
                    >Bid {fmt(row.dydxBid)}</button>
                    <button onClick={() => setDydxPrice(priceStr((row.dydxBid + row.dydxAsk) / 2))}
                      className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${dydxPrice === priceStr((row.dydxBid + row.dydxAsk) / 2) ? 'bg-[#4A9FE8]/20 border-[#4A9FE8]/60 text-[#4A9FE8]' : 'bg-[#061530] border-[#0a2560] text-blue-500 hover:border-[#4A9FE8]/60 hover:text-[#4A9FE8]'}`}
                    >Mid {fmt((row.dydxBid + row.dydxAsk) / 2)}</button>
                    <button onClick={() => setDydxPrice(priceStr(row.dydxAsk))}
                      className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${dydxPrice === priceStr(row.dydxAsk) ? 'bg-rose-500/20 border-rose-500/60 text-rose-300' : 'bg-[#061530] border-[#0a2560] text-blue-500 hover:border-rose-600 hover:text-rose-400'}`}
                    >Ask {fmt(row.dydxAsk)}</button>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-blue-500">Miktar</span>
                <span className="font-mono text-white">{card.dydxSize.toFixed(6)} {card.base}</span>
              </div>
            </div>

            {/* Bluefin */}
            <div className={`rounded-xl border p-3 space-y-2 ${dirBg(bfCloseDir)}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-cyan-400 uppercase tracking-wider">Bluefin</span>
                <span className="text-[10px] text-blue-600">maker</span>
              </div>
              <div className="text-[11px]">{dirLabel(bfCloseDir)}</div>
              <div>
                <input
                  type="number" value={bfPrice}
                  onChange={e => setBfPrice(e.target.value)}
                  className="w-full bg-[#061530] border border-[#0a2560] rounded-lg px-2.5 py-1.5 text-xs font-mono text-white focus:border-[#4A9FE8] outline-none"
                  placeholder="Limit fiyat"
                />
                {row && (
                  <div className="flex gap-1 mt-1">
                    <button onClick={() => setBfPrice(priceStr(row.bfBid))}
                      className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${bfPrice === priceStr(row.bfBid) ? 'bg-emerald-500/20 border-emerald-500/60 text-emerald-300' : 'bg-[#061530] border-[#0a2560] text-cyan-700 hover:border-emerald-600 hover:text-emerald-400'}`}
                    >Bid {fmt(row.bfBid)}</button>
                    <button onClick={() => setBfPrice(priceStr((row.bfBid + row.bfAsk) / 2))}
                      className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${bfPrice === priceStr((row.bfBid + row.bfAsk) / 2) ? 'bg-[#4A9FE8]/20 border-[#4A9FE8]/60 text-[#4A9FE8]' : 'bg-[#061530] border-[#0a2560] text-cyan-700 hover:border-[#4A9FE8]/60 hover:text-[#4A9FE8]'}`}
                    >Mid {fmt((row.bfBid + row.bfAsk) / 2)}</button>
                    <button onClick={() => setBfPrice(priceStr(row.bfAsk))}
                      className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${bfPrice === priceStr(row.bfAsk) ? 'bg-rose-500/20 border-rose-500/60 text-rose-300' : 'bg-[#061530] border-[#0a2560] text-cyan-700 hover:border-rose-600 hover:text-rose-400'}`}
                    >Ask {fmt(row.bfAsk)}</button>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-blue-500">Miktar</span>
                <span className="font-mono text-white">{card.bfSize.toFixed(6)} {card.base}</span>
              </div>
            </div>
          </div>

          {/* Maker / Taker toggle */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-blue-500">Emir tipi</span>
            <div className="flex rounded-lg border border-[#0a2560] overflow-hidden text-[10px] font-bold">
              <button onClick={() => setPostOnly(true)}
                className={`px-3 py-1.5 transition-colors ${postOnly ? 'bg-emerald-500/20 text-emerald-300' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}
              >Maker (Post-Only)</button>
              <button onClick={() => setPostOnly(false)}
                className={`px-3 py-1.5 border-l border-[#0a2560] transition-colors ${!postOnly ? 'bg-rose-500/20 text-rose-300' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}
              >Taker (Limit)</button>
            </div>
          </div>

          <div className="flex items-center gap-1.5 text-[10px] text-yellow-600">
            <AlertTriangle size={11} className="flex-shrink-0" />
            <span>{postOnly ? 'Post-only reddedilebilir, fiyatı düzenle veya Taker\'a geç.' : 'Taker emir piyasada dolar, yüksek fee uygulanır.'}</span>
          </div>

          {/* Buttons */}
          <div className="flex gap-2.5">
            <button onClick={onClose} disabled={sending}
              className="flex-1 py-2 rounded-xl border border-[#0a2560] text-blue-400 text-xs font-semibold hover:bg-[#0a2560]/40 transition-colors disabled:opacity-40"
            >İptal</button>
            <button
              disabled={sending || !canSend}
              onClick={async () => {
                if (sending) return;
                setSending(true);
                try {
                  sendWs({
                    type: 'manual_arb_order',
                    payload: {
                      post_only: postOnly,
                      dydx: {
                        symbol:   card.dydxTkr,
                        side:     dydxCloseDir.toLowerCase(),
                        price:    parseFloat(dydxPrice),
                        quantity: parseFloat(card.dydxSize.toFixed(8)),
                        leverage: 1,
                      },
                      bf: {
                        symbol:   card.bfSymbol,
                        side:     bfCloseDir.toLowerCase(),
                        price:    parseFloat(bfPrice),
                        quantity: parseFloat(card.bfSize.toFixed(8)),
                        leverage: 1,
                      },
                    },
                  });
                  await new Promise<void>((resolve) => {
                    const handler = (e: MessageEvent) => {
                      try {
                        const d = JSON.parse((e as any).data ?? e);
                        if (d.type === 'arb_order_result') {
                          window.removeEventListener('ws_message', handler as any);
                          if (d.payload?.success) {
                            showToast('✅ Kapatma Emirleri Gönderildi', `dYdX + BF ${card.base} kapatılıyor`, 'success');
                            onRecordClose?.(
                              card.base,
                              card.dydxSide.toLowerCase(),
                              Date.now(),
                              parseFloat(dydxPrice),
                              parseFloat(bfPrice),
                            );
                            onClose();
                          } else {
                            const dErr = d.payload?.dydx?.error;
                            const bErr = d.payload?.bf?.error;
                            showToast('Emir Hatası', [dErr, bErr].filter(Boolean).join(' | '), 'error');
                          }
                          resolve();
                        }
                      } catch {}
                    };
                    window.addEventListener('ws_message', handler as any);
                    setTimeout(() => { window.removeEventListener('ws_message', handler as any); resolve(); }, 20000);
                  });
                } finally {
                  setSending(false);
                }
              }}
              className="flex-1 py-2 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-rose-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sending
                ? <><Loader2 size={13} className="animate-spin" /> Gönderiliyor...</>
                : <><X size={13} /> Her İki Pozisyonu Kapat</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ARB History (localStorage) ───────────────────────────────────────────────
const HISTORY_KEY = 'dydxbf_arb_v1';

interface ArbRecord {
  id: string;
  base: string;
  dydxSymbol: string;
  bfSymbol: string;
  openTime: number;
  dydxDir: 'long' | 'short';
  bfDir: string;
  qty: number;             // token miktarı
  dydxOpenPrice: number;
  bfOpenPrice: number;
  postOnly: boolean;
  closeTime?: number;
  dydxClosePrice?: number;
  bfClosePrice?: number;
  status: 'open' | 'closed';
}

function loadHistory(): ArbRecord[] {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); }
  catch { return []; }
}
function saveHistory(records: ArbRecord[]) {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(records)); } catch {}
}

// ─── Main page ────────────────────────────────────────────────────────────────
type Tab = 'dashboard' | 'performans' | 'gecmis';

export default function DydxBf({ dark }: { dark: boolean }) {
  const t = getTheme(dark);
  const { sendWs, subscribe, botsState } = useWS() as any;
  const [tab,        setTab]        = useState<Tab>('dashboard');
  const [rows,       setRows]       = useState<Row[]>([]);
  const [bfMaker,    setBfMaker]    = useState<Record<string, number>>({});
  const [bfTaker,    setBfTaker]    = useState<Record<string, number>>({});
  const [loading,    setLoading]    = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [error,      setError]      = useState<string | null>(null);
  const [modalRow,   setModalRow]   = useState<Row | null>(null);

  // ── Performans state ───────────────────────────────────────────────────────
  const [dydxAddr,   setDydxAddr]   = useState('');
  const [dydxPos,    setDydxPos]    = useState<DydxPos[]>([]);
  const [dydxFeeMap, setDydxFeeMap] = useState<Record<string, number>>({});
  const [bfTrades,   setBfTrades]   = useState<any[]>([]);
  const [bfFunding,  setBfFunding]  = useState<any[]>([]);
  const [perfLoad,   setPerfLoad]   = useState(false);
  const [closeCard,  setCloseCard]  = useState<CloseCard | null>(null);
  const [arbHistory, setArbHistory] = useState<ArbRecord[]>(() => loadHistory());

  const handleRecordOpen = useCallback((r: ArbRecord) => {
    setArbHistory(prev => { const u = [...prev, r]; saveHistory(u); return u; });
  }, []);

  const handleRecordClose = useCallback((base: string, dydxDir: string, closeTime: number, dydxClosePrice: number, bfClosePrice: number) => {
    setArbHistory(prev => {
      // En son eşleşen açık kaydı kapat
      let closed = false;
      const u = [...prev].reverse().map(r => {
        if (!closed && r.base === base && r.dydxDir === dydxDir && r.status === 'open') {
          closed = true;
          return { ...r, closeTime, dydxClosePrice, bfClosePrice, status: 'closed' as const };
        }
        return r;
      }).reverse();
      saveHistory(u);
      return u;
    });
  }, []);

  const bfPositions: any[] = (botsState?.bot2?.positions ?? []);

  // Subscribe to WS for performance data
  useEffect(() => {
    return subscribe((data: any) => {
      if (data.type === 'dydx_address') {
        setDydxAddr(data.payload?.address || '');
      }
      if (data.type === 'sqlite_history') {
        setBfTrades(data.payload?.trades || []);
        setBfFunding(data.payload?.funding_fees || []);
      }
    });
  }, [subscribe]);

  // When switching to performans or gecmis tab, request data
  useEffect(() => {
    if (tab !== 'performans' && tab !== 'gecmis') return;
    setPerfLoad(true);
    setDydxAddr('');
    setDydxPos([]);
    setDydxFeeMap({});
    setBfTrades([]);
    setBfFunding([]);
    sendWs({ type: 'get_dydx_address' });
    sendWs({ type: 'get_sqlite_history', payload: { bot_id: 'bot2', limit: 50000 } });
  }, [tab]); // eslint-disable-line

  // When dYdX address arrives, fetch open positions + fills
  useEffect(() => {
    if (!dydxAddr || tab !== 'performans') return;
    (async () => {
      try {
        const r = await fetch(
          `${DYDX_API}/perpetualPositions?address=${dydxAddr}&subaccountNumber=0&status=OPEN&limit=50`
        );
        const d = await r.json();
        const positions: DydxPos[] = d.positions || [];
        setDydxPos(positions);
        // Fetch fills for each open position to get fee total
        const feeEntries = await Promise.all(
          positions.map(async (p) => {
            try {
              const fr = await fetch(
                `${DYDX_API}/fills?address=${dydxAddr}&subaccountNumber=0&market=${p.market}&limit=100`
              );
              const fd = await fr.json();
              const fee = (fd.fills || []).reduce(
                (s: number, f: any) => s + parseFloat(f.fee || '0'), 0
              );
              return [p.market, fee] as [string, number];
            } catch { return [p.market, 0] as [string, number]; }
          })
        );
        setDydxFeeMap(Object.fromEntries(feeEntries));
      } catch (e) {
        console.error('dYdX positions fetch error:', e);
      } finally {
        setPerfLoad(false);
      }
    })();
  }, [dydxAddr]); // eslint-disable-line

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const bfRes  = await fetch(`${BF_PROXY}/tickers`);
      const bfData: BfTicker[] = await bfRes.json();

      const infoRes  = await fetch(`${BF_PROXY}/info`);
      const infoData = await infoRes.json();
      const mkrMap:    Record<string, number> = {};
      const tkrMap:    Record<string, number> = {};
      const bfMaxLevMap: Record<string, number> = {};
      (infoData.markets || []).forEach((m: BfMarketInfo) => {
        const base = m.symbol.replace('-PERP', '');
        mkrMap[base]    = e9(m.defaultMakerFeeE9);
        tkrMap[base]    = e9(m.defaultTakerFeeE9);
        const imr = parseFloat(m.initialMarginRatioE9 || '0');
        bfMaxLevMap[base] = imr > 0 ? Math.floor(1e9 / imr) : 0;
      });
      setBfMaker(mkrMap);
      setBfTaker(tkrMap);

      const dydxRes  = await fetch(`${DYDX_API}/perpetualMarkets`);
      const dydxData = await dydxRes.json();
      const dydxMap: Record<string, DydxMarket> = dydxData.markets;

      const commonBf = bfData.filter(bf => {
        const base = bf.symbol.replace('-PERP', '');
        return BF_TO_DYDX[base] && dydxMap[BF_TO_DYDX[base]];
      });

      const obResults = await Promise.all(
        commonBf.map(async bf => {
          const base    = bf.symbol.replace('-PERP', '');
          const dydxTkr = BF_TO_DYDX[base];
          try {
            const r  = await fetch(`${DYDX_API}/orderbooks/perpetualMarket/${dydxTkr}`);
            const d: DydxOrderbook = await r.json();
            return { base, ob: d };
          } catch { return { base, ob: null }; }
        })
      );
      const obMap: Record<string, DydxOrderbook | null> = {};
      obResults.forEach(x => { obMap[x.base] = x.ob; });

      const built: Row[] = commonBf.map(bf => {
        const base    = bf.symbol.replace('-PERP', '');
        const dydxTkr = BF_TO_DYDX[base];
        const dydxMkt = dydxMap[dydxTkr];
        const ob      = obMap[base];

        const bfBid    = e9(bf.bestBidPriceE9);
        const bfAsk    = e9(bf.bestAskPriceE9);
        const bfMid    = (bfBid + bfAsk) / 2;
        const bfSpread = bfMid > 0 ? (bfAsk - bfBid) / bfMid : 0;

        const dydxBid  = ob?.bids?.[0] ? parseFloat(ob.bids[0].price) : parseFloat(dydxMkt.oraclePrice);
        const dydxAsk  = ob?.asks?.[0] ? parseFloat(ob.asks[0].price) : parseFloat(dydxMkt.oraclePrice);
        const dydxMid  = (dydxBid + dydxAsk) / 2;
        const dydxSprd = dydxMid > 0 ? (dydxAsk - dydxBid) / dydxMid : 0;

        const bfFunding    = e9(bf.lastFundingRateE9);
        const bfFundingEst = e9(bf.estimatedFundingRateE9);
        const dydxFunding  = parseFloat(dydxMkt.nextFundingRate);

        const netS_D_L_B = dydxFunding - bfFundingEst;
        const netL_D_S_B = bfFundingEst - dydxFunding;

        let bestRate = 0;
        let bestDir: Row['bestDir'] = 'NONE';
        if (netS_D_L_B >= netL_D_S_B && netS_D_L_B > 0) { bestRate = netS_D_L_B; bestDir = 'S_DYDX_L_BF'; }
        else if (netL_D_S_B > 0)                          { bestRate = netL_D_S_B; bestDir = 'L_DYDX_S_BF'; }

        return {
          base, bfSymbol: bf.symbol, dydxSymbol: dydxTkr,
          bfBid, bfAsk, bfMidSpreadPct: bfSpread,
          bfFunding, bfFundingEst,
          bfMaker: mkrMap[base] ?? 0, bfTaker: tkrMap[base] ?? 0,
          dydxBid, dydxAsk, dydxMidSpreadPct: dydxSprd, dydxFunding,
          netShortDydxLongBf: netS_D_L_B, netLongDydxShortBf: netL_D_S_B,
          bestRate, bestDir,
          dydxOracle: parseFloat(dydxMkt.oraclePrice),
          bfOracle: e9(bf.oraclePriceE9 || '0'),
          dydxMaxLev: (() => { const imf = parseFloat(dydxMkt.initialMarginFraction || '0'); return imf > 0 ? Math.floor(1 / imf) : 0; })(),
          bfMaxLev: bfMaxLevMap[base] ?? 0,
        };
      });

      built.sort((a, b) => b.bestRate - a.bestRate);
      setRows(built);
      setLastUpdate(new Date());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const id = setInterval(fetchData, 30_000);
    return () => clearInterval(id);
  }, [fetchData]);

  const tabClass = (active: boolean) =>
    `px-4 py-2 text-xs font-semibold uppercase tracking-widest rounded-t-lg transition-colors ${
      active
        ? 'bg-[#0a2560] text-[#4A9FE8] border-b-2 border-[#4A9FE8]'
        : 'text-blue-500 hover:text-blue-300'
    }`;

  return (
    <div className={`min-h-screen ${t.bg} ${t.text} p-6`}>

      {/* Modals */}
      {modalRow && (
        <OrderModal row={modalRow} dark={dark} onClose={() => setModalRow(null)} sendWs={sendWs} onRecordOpen={handleRecordOpen} />
      )}
      {closeCard && (
        <CloseModal card={closeCard} dark={dark} onClose={() => setCloseCard(null)} sendWs={sendWs} onRecordClose={handleRecordClose} />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-black tracking-widest text-[#4A9FE8] uppercase">dYdX ↔ Bluefin</h1>
          <p className="text-xs text-blue-500 mt-0.5">Cross-DEX Funding Arbitrage Scanner</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdate && (
            <span className="text-[11px] text-blue-600">{lastUpdate.toLocaleTimeString('tr-TR')} güncellendi</span>
          )}
          <button
            onClick={() => { setLoading(true); fetchData(); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0a2560] hover:bg-[#0d3080] text-blue-300 text-xs transition-colors"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Yenile
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#0a2560] mb-5">
        <button className={tabClass(tab === 'dashboard')}  onClick={() => setTab('dashboard')}>Dashboard</button>
        <button className={tabClass(tab === 'performans')} onClick={() => setTab('performans')}>Performans</button>
        <button className={tabClass(tab === 'gecmis')}     onClick={() => setTab('gecmis')}>Geçmiş</button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-rose-900/20 border border-rose-500/30 text-rose-400 text-xs">⚠ {error}</div>
      )}

      {/* ── DASHBOARD ─────────────────────────────────────────────────── */}
      {tab === 'dashboard' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 max-w-sm">
            <FeeCard label="dYdX Fees" maker={DYDX_MAKER} taker={DYDX_TAKER} dark={dark} />
            <FeeCard
              label="Bluefin Fees"
              maker={Object.values(bfMaker)[0] ?? 0}
              taker={Object.values(bfTaker)[0] ?? 0}
              dark={dark}
            />
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-blue-500 text-sm py-8 justify-center">
              <RefreshCw size={14} className="animate-spin" /> Veriler yükleniyor...
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-[#0a2560]">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#0a2560]">
                    <th className="text-left px-3 py-2.5 text-[#4A9FE8] font-semibold uppercase tracking-wider">Asset</th>
                    <th className="text-right px-2 py-2.5 text-blue-400 font-semibold">dYdX Bid</th>
                    <th className="text-right px-2 py-2.5 text-blue-400 font-semibold">dYdX Ask</th>
                    <th className="text-right px-2 py-2.5 text-blue-400 font-semibold">Spread</th>
                    <th className="text-right px-3 py-2.5 text-blue-400 font-semibold">Funding 1h</th>
                    <th className="px-1" />
                    <th className="text-right px-2 py-2.5 text-cyan-400 font-semibold">BF Bid</th>
                    <th className="text-right px-2 py-2.5 text-cyan-400 font-semibold">BF Ask</th>
                    <th className="text-right px-2 py-2.5 text-cyan-400 font-semibold">Spread</th>
                    <th className="text-right px-3 py-2.5 text-cyan-400 font-semibold">Funding 1h</th>
                    <th className="px-1" />
                    <th className="text-right px-3 py-2.5 text-[#4A9FE8] font-semibold">Best Arb / h</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr
                      key={row.base}
                      className={`border-b border-[#0a2560]/50 hover:bg-[#0a2560]/30 transition-colors ${
                        row.bestRate > 0.0001 ? 'bg-emerald-950/10' : ''
                      }`}
                    >
                      <td className="px-3 py-2.5">
                        <div className="font-bold text-white">{row.base}</div>
                        <div className="text-[9px] text-blue-600">{row.bfSymbol} / {row.dydxSymbol}</div>
                      </td>
                      <td className="text-right px-2 py-2.5 font-mono text-blue-200">{fmt(row.dydxBid)}</td>
                      <td className="text-right px-2 py-2.5 font-mono text-blue-200">{fmt(row.dydxAsk)}</td>
                      <td className="text-right px-2 py-2.5 font-mono text-blue-400 text-[11px]">{pct(row.dydxMidSpreadPct, 4)}</td>
                      <td className="text-right px-3 py-2.5"><FundingBadge rate={row.dydxFunding} /></td>
                      <td className="px-1"><div className="w-px h-8 bg-[#0a2560] mx-auto" /></td>
                      <td className="text-right px-2 py-2.5 font-mono text-cyan-200">{fmt(row.bfBid)}</td>
                      <td className="text-right px-2 py-2.5 font-mono text-cyan-200">{fmt(row.bfAsk)}</td>
                      <td className="text-right px-2 py-2.5 font-mono text-cyan-400 text-[11px]">{pct(row.bfMidSpreadPct, 4)}</td>
                      <td className="text-right px-3 py-2.5">
                        <div className="flex flex-col items-end gap-0.5">
                          <FundingBadge rate={row.bfFundingEst} />
                          <span className="text-[9px] text-blue-600">last: <span className="font-mono">{pct(row.bfFunding, 5)}</span></span>
                        </div>
                      </td>
                      <td className="px-1"><div className="w-px h-8 bg-[#0a2560] mx-auto" /></td>
                      <td className="text-right px-3 py-2.5">
                        <ArbButton row={row} onOpen={setModalRow} />
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={12} className="text-center py-8 text-blue-600">Ortak market bulunamadı</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap gap-4 text-[10px] text-blue-600 pt-1">
            <span>↑ Funding pozitif → longlar öder, shortlar alır</span>
            <span>↓ Funding negatif → shortlar öder, longlar alır</span>
            <span className="text-emerald-600">Best Arb = tahmini kazanç oranı / saat (spread ve fee dahil değil)</span>
          </div>
        </div>
      )}

      {/* ── PERFORMANS ────────────────────────────────────────────────── */}
      {tab === 'gecmis' && (() => {
        const fmtP   = (n: number) => { const s = n >= 0 ? '+' : ''; return `${s}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
        const pnlCls = (n: number) => n >= 0 ? 'text-emerald-400' : 'text-rose-400';
        const dur    = (ms: number) => {
          const h = Math.floor(ms / 3600000);
          const m = Math.floor((ms % 3600000) / 60000);
          return h > 0 ? `${h}s ${m}dk` : `${m}dk`;
        };

        // Auto-import: mevcut açık pozisyonları arbHistory'de yoksa ekle
        const autoImport = () => {
          const toAdd: ArbRecord[] = [];
          bfPositions.forEach((bfp: any) => {
            const base    = String(bfp.symbol || '').replace('-PERP', '');
            const dydxTkr = BF_TO_DYDX[base];
            if (!dydxTkr) return;
            const dydxP = dydxPos.find(p => p.market === dydxTkr);
            if (!dydxP) return;
            const dydxDir = dydxP.side.toLowerCase() as 'long' | 'short';
            // Zaten açık kayıt var mı?
            const exists = arbHistory.some(r => r.base === base && r.dydxDir === dydxDir && r.status === 'open');
            if (exists) return;
            toAdd.push({
              id:            `import-${base}-${dydxP.createdAt}`,
              base,
              dydxSymbol:    dydxTkr,
              bfSymbol:      bfp.symbol,
              openTime:      new Date(dydxP.createdAt).getTime(),
              dydxDir,
              bfDir:         bfp.side,
              qty:           parseFloat(dydxP.size),
              dydxOpenPrice: parseFloat(dydxP.entryPrice),
              bfOpenPrice:   bfp.avgEntryPrice || 0,
              postOnly:      true,
              status:        'open',
            });
          });
          if (toAdd.length > 0) {
            setArbHistory(prev => { const u = [...prev, ...toAdd]; saveHistory(u); return u; });
          }
        };

        // Veri yüklendiyse ve import gerekiyorsa çalıştır
        if (!perfLoad && (bfPositions.length > 0 || dydxPos.length > 0)) autoImport();

        // Enrich each record with BF fees/funding from SQLite
        const enriched = arbHistory.map(r => {
          const closeT = r.closeTime ?? Date.now();
          const bfFee = bfTrades
            .filter(t => t.symbol?.includes(r.base) && (t.executedAtMillis || 0) >= r.openTime && (t.executedAtMillis || 0) <= closeT)
            .reduce((s: number, t: any) => s + Math.abs(t.fee || 0), 0);
          const bfFundingAmt = bfFunding
            .filter(f => f.symbol?.includes(r.base) && (f.createdTime || 0) >= r.openTime && (f.createdTime || 0) <= closeT)
            .reduce((s: number, f: any) => s + (f.fundingFee || 0), 0);

          // dYdX fee estimate: qty × avgPrice × feeRate × 2 (entry + exit)
          const avgDydx   = ((r.dydxOpenPrice || 0) + (r.dydxClosePrice || r.dydxOpenPrice || 0)) / 2;
          const dydxFeeEst = r.qty * avgDydx * (r.postOnly ? DYDX_MAKER : DYDX_TAKER) * (r.status === 'closed' ? 2 : 1);

          // Price PnL (only for closed)
          let dydxPricePnl = 0, bfPricePnl = 0;
          if (r.status === 'closed' && r.dydxClosePrice && r.bfClosePrice) {
            dydxPricePnl = r.dydxDir === 'long'
              ? (r.dydxClosePrice - r.dydxOpenPrice) * r.qty
              : (r.dydxOpenPrice  - r.dydxClosePrice) * r.qty;
            bfPricePnl = r.bfDir === 'long'
              ? (r.bfClosePrice - r.bfOpenPrice) * r.qty
              : (r.bfOpenPrice  - r.bfClosePrice) * r.qty;
          }
          const pricePnl  = dydxPricePnl + bfPricePnl;
          const totalFee  = bfFee + dydxFeeEst;
          const netPnl    = pricePnl - totalFee + bfFundingAmt;
          const elapsed   = closeT - r.openTime;

          return { ...r, bfFee, bfFunding: bfFundingAmt, dydxFeeEst, pricePnl, totalFee, netPnl, elapsed };
        });

        const closed   = enriched.filter(r => r.status === 'closed');
        const totalNet = closed.reduce((s, r) => s + r.netPnl, 0);
        const winCount = closed.filter(r => r.netPnl > 0).length;

        return (
          <div className="space-y-4">
            {/* Summary bar */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Toplam Net PnL', value: fmtP(totalNet), cls: pnlCls(totalNet) },
                { label: 'Kapalı İşlem', value: `${closed.length}`, cls: 'text-white' },
                { label: 'Kazanma Oranı', value: closed.length > 0 ? `${((winCount / closed.length) * 100).toFixed(0)}%` : '—', cls: 'text-[#4A9FE8]' },
              ].map(c => (
                <div key={c.label} className="rounded-xl border border-[#0a2560] bg-[#030d1f] px-4 py-2.5 text-center">
                  <p className="text-[10px] text-blue-500 mb-1">{c.label}</p>
                  <p className={`text-base font-black font-mono ${c.cls}`}>{c.value}</p>
                </div>
              ))}
            </div>

            {arbHistory.length === 0 ? (
              <div className="rounded-xl border border-[#0a2560] p-10 text-center text-blue-600 text-sm">
                Henüz kayıtlı işlem yok. Emir gönderdiğinizde otomatik kaydedilir.
              </div>
            ) : (
              <div className="rounded-xl border border-[#0a2560] bg-[#030d1f] overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-[#0a2560] bg-[#061530]">
                      <th className="text-left px-3 py-2 text-blue-500 font-semibold">Varlık</th>
                      <th className="text-left px-3 py-2 text-blue-500 font-semibold">Yön</th>
                      <th className="text-right px-3 py-2 text-blue-500 font-semibold">Miktar</th>
                      <th className="text-right px-3 py-2 text-blue-300 font-semibold">dYdX Giriş→Çıkış</th>
                      <th className="text-right px-3 py-2 text-cyan-400 font-semibold">BF Giriş→Çıkış</th>
                      <th className="text-right px-3 py-2 text-blue-500 font-semibold">Fiyat PnL</th>
                      <th className="text-right px-3 py-2 text-rose-400 font-semibold">Fee</th>
                      <th className="text-right px-3 py-2 text-emerald-400 font-semibold">Funding</th>
                      <th className="text-right px-3 py-2 text-[#4A9FE8] font-semibold">Net PnL</th>
                      <th className="text-right px-3 py-2 text-blue-500 font-semibold">Süre</th>
                      <th className="px-2 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {[...enriched].reverse().map(r => {
                      const priceStr = (p: number) => p > 0 ? fmt(p) : '—';
                      return (
                        <tr key={r.id} className="border-b border-[#0a2560]/50 hover:bg-[#0a2560]/20 transition-colors">
                          <td className="px-3 py-2">
                            <div className="font-bold text-white">{r.base}</div>
                            <div className={`text-[9px] ${r.status === 'open' ? 'text-emerald-500' : 'text-blue-600'}`}>
                              {r.status === 'open' ? '● Açık' : '○ Kapalı'} · {new Date(r.openTime).toLocaleDateString('tr-TR')}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className={`text-[10px] font-bold ${r.dydxDir === 'long' ? 'text-emerald-400' : 'text-rose-400'}`}>
                              dYdX {r.dydxDir.toUpperCase()}
                            </div>
                            <div className={`text-[10px] font-bold ${r.bfDir === 'long' ? 'text-emerald-400' : 'text-rose-400'}`}>
                              BF {r.bfDir.toUpperCase()}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-blue-200">
                            {r.qty.toFixed(4)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">
                            <span className="text-blue-300">{priceStr(r.dydxOpenPrice)}</span>
                            {r.dydxClosePrice ? <><span className="text-blue-600 mx-1">→</span><span className="text-blue-200">{priceStr(r.dydxClosePrice)}</span></> : <span className="text-blue-600 ml-1">→ …</span>}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">
                            <span className="text-cyan-600">{priceStr(r.bfOpenPrice)}</span>
                            {r.bfClosePrice ? <><span className="text-blue-600 mx-1">→</span><span className="text-cyan-400">{priceStr(r.bfClosePrice)}</span></> : <span className="text-blue-600 ml-1">→ …</span>}
                          </td>
                          <td className={`px-3 py-2 text-right font-mono ${r.status === 'closed' ? pnlCls(r.pricePnl) : 'text-blue-600'}`}>
                            {r.status === 'closed' ? fmtP(r.pricePnl) : '—'}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-rose-400">
                            −${r.totalFee.toFixed(2)}
                            <span className="text-blue-700 text-[9px] ml-0.5">est</span>
                          </td>
                          <td className={`px-3 py-2 text-right font-mono ${pnlCls(r.bfFunding)}`}>
                            {r.bfFunding !== 0 ? fmtP(r.bfFunding) : <span className="text-blue-600">$0.00</span>}
                          </td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${r.status === 'closed' ? pnlCls(r.netPnl) : 'text-blue-600'}`}>
                            {r.status === 'closed' ? fmtP(r.netPnl) : '—'}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-blue-500">
                            {r.status === 'closed' && r.closeTime ? dur(r.elapsed) : '—'}
                          </td>
                          <td className="px-2 py-2">
                            <button
                              onClick={() => {
                                const u = arbHistory.filter(x => x.id !== r.id);
                                setArbHistory(u); saveHistory(u);
                              }}
                              className="text-blue-700 hover:text-rose-400 transition-colors p-0.5"
                              title="Sil"
                            ><X size={11} /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {arbHistory.length > 0 && (
              <div className="flex justify-end">
                <button
                  onClick={() => { if (window.confirm('Tüm geçmiş silinsin mi?')) { setArbHistory([]); saveHistory([]); } }}
                  className="text-[10px] text-blue-700 hover:text-rose-400 transition-colors"
                >Tümünü Temizle</button>
              </div>
            )}
          </div>
        );
      })()}

      {tab === 'performans' && (
        <div className="space-y-4">
          {/* Refresh button */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-blue-500">Açık ARB pozisyonları — iki borsadan eşleşen varlık bazında</p>
            <button
              onClick={() => {
                setPerfLoad(true);
                setDydxAddr('');
                setDydxPos([]);
                setDydxFeeMap({});
                setBfTrades([]);
                setBfFunding([]);
                sendWs({ type: 'get_dydx_address' });
                sendWs({ type: 'get_sqlite_history', payload: { bot_id: 'bot2', limit: 10000 } });
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0a2560] hover:bg-[#0d3080] text-blue-300 text-xs transition-colors"
            >
              <RefreshCw size={12} className={perfLoad ? 'animate-spin' : ''} />
              Yenile
            </button>
          </div>

          {perfLoad && (
            <div className="flex items-center gap-2 text-blue-500 text-sm py-8 justify-center">
              <RefreshCw size={14} className="animate-spin" /> Veriler yükleniyor...
            </div>
          )}

          {!perfLoad && (() => {
            // Build matched cards: BF position + dYdX position per asset
            const cards = bfPositions.map((bfp: any) => {
              const base      = String(bfp.symbol || '').replace('-PERP', '');
              const dydxTkr   = BF_TO_DYDX[base];
              if (!dydxTkr) return null;
              const dydxP = dydxPos.find(p => p.market === dydxTkr);
              if (!dydxP) return null;

              const posOpenedAt  = new Date(dydxP.createdAt).getTime();
              const dydxFee      = dydxFeeMap[dydxTkr] || 0;
              const bfFee        = bfTrades
                .filter(t => (t.symbol === bfp.symbol || t.symbol?.includes(base))
                          && (t.executedAtMillis || 0) >= posOpenedAt)
                .reduce((s: number, t: any) => s + Math.abs(t.fee || 0), 0);
              const dydxFunding  = parseFloat(dydxP.netFunding || '0');
              const bfFundingNet = bfFunding
                .filter(f => (f.symbol === bfp.symbol || f.symbol?.includes(base))
                          && (f.createdTime || 0) >= posOpenedAt)
                .reduce((s: number, f: any) => s + (f.fundingFee || 0), 0);

              const dydxUPnl  = parseFloat(dydxP.unrealizedPnl || '0');
              const bfUPnl    = bfp.unrealizedPnl || 0;
              const netUPnl   = dydxUPnl + bfUPnl;
              const netAllIn  = netUPnl + dydxFunding + bfFundingNet - dydxFee - bfFee;

              return { base, dydxTkr, bfp, dydxP, dydxFee, bfFee, dydxFunding, bfFundingNet, dydxUPnl, bfUPnl, netUPnl, netAllIn };
            }).filter(Boolean) as any[];

            if (cards.length === 0) {
              return (
                <div className="rounded-xl border border-[#0a2560] p-10 text-center text-blue-600 text-sm">
                  {bfPositions.length === 0 && dydxPos.length === 0
                    ? 'Her iki borsada da açık pozisyon bulunamadı.'
                    : 'Eşleşen ARB pozisyon çifti bulunamadı.'}
                </div>
              );
            }

            const fmtPnl = (n: number) => {
              const s = n >= 0 ? '+' : '';
              return `${s}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            };
            const pnlColor = (n: number) => n >= 0 ? 'text-emerald-400' : 'text-rose-400';

            return cards.map((card: any) => {
              const matchRow = rows.find(r => r.base === card.base);
              const fmtBidAsk = (b: number, a: number) => {
                const dec = b >= 1000 ? 0 : b >= 10 ? 2 : 4;
                return `${b.toFixed(dec)} / ${a.toFixed(dec)}`;
              };
              return (
              <div key={card.base} className="rounded-2xl border border-[#0a2560] bg-[#030d1f] overflow-hidden">

                {/* Card header */}
                <div className="bg-[#061530] border-b border-[#0a2560]">
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span className="text-base font-black text-[#4A9FE8]">{card.base}</span>
                      <span className="text-[10px] text-blue-500 bg-[#0a2560] px-2 py-0.5 rounded-full">
                        dYdX {card.dydxP.side} · BF {card.bfp.side?.toUpperCase()}
                      </span>
                      <span className="text-[10px] text-blue-600">
                        {new Date(card.dydxP.createdAt).toLocaleDateString('tr-TR')} girişi
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-blue-500 mb-0.5">Net Unrealized PnL</p>
                      <p className={`text-base font-black ${pnlColor(card.netUPnl)}`}>
                        {fmtPnl(card.netUPnl)}
                      </p>
                    </div>
                  </div>
                  {/* Oracle + Bid/Ask bar */}
                  {matchRow && (
                    <div className="flex items-center gap-3 px-4 py-1.5 border-t border-[#0a2560]/50 text-[11px] flex-wrap">
                      {/* dYdX */}
                      <span className="text-blue-400 font-bold">dYdX</span>
                      <span className="text-blue-500">Oracle</span>
                      <span className="font-mono text-blue-200">${fmt(matchRow.dydxOracle)}</span>
                      <span className="text-blue-500">Bid/Ask</span>
                      <span className="font-mono">
                        <span className="text-emerald-400">{fmtBidAsk(matchRow.dydxBid, matchRow.dydxAsk).split(' / ')[0]}</span>
                        <span className="text-blue-600">/</span>
                        <span className="text-rose-400">{fmtBidAsk(matchRow.dydxBid, matchRow.dydxAsk).split(' / ')[1]}</span>
                      </span>
                      <span className="text-blue-700 mx-1">|</span>
                      {/* Bluefin */}
                      <span className="text-cyan-400 font-bold">BF</span>
                      <span className="text-blue-500">Oracle</span>
                      <span className="font-mono text-blue-200">${fmt(matchRow.bfOracle)}</span>
                      <span className="text-blue-500">Bid/Ask</span>
                      <span className="font-mono">
                        <span className="text-emerald-400">{fmtBidAsk(matchRow.bfBid, matchRow.bfAsk).split(' / ')[0]}</span>
                        <span className="text-blue-600">/</span>
                        <span className="text-rose-400">{fmtBidAsk(matchRow.bfBid, matchRow.bfAsk).split(' / ')[1]}</span>
                      </span>
                      <span className="text-blue-700 mx-1">|</span>
                      <span className="text-blue-500">Toplam Fee</span>
                      <span className="font-mono text-rose-400">−${(card.dydxFee + card.bfFee).toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Two legs */}
                <div className="grid grid-cols-2 gap-0 divide-x divide-[#0a2560]">
                  {/* dYdX leg */}
                  <div className="p-3 space-y-1.5">
                    <p className="text-[10px] font-bold text-blue-300 uppercase tracking-widest mb-2">dYdX</p>
                    {[
                      ['Yön',       <span className={card.dydxP.side === 'LONG' ? 'text-emerald-400' : 'text-rose-400'}>{card.dydxP.side}</span>],
                      ['Miktar',    `${parseFloat(card.dydxP.size).toFixed(4)} ${card.base}`],
                      ['Giriş',     `$${fmt(parseFloat(card.dydxP.entryPrice))}`],
                      ['uPnL',      <span className={pnlColor(card.dydxUPnl)}>{fmtPnl(card.dydxUPnl)}</span>],
                    ].map(([label, val]) => (
                      <div key={label as string} className="flex items-center justify-between text-[11px]">
                        <span className="text-blue-500">{label}</span>
                        <span className="font-mono text-white">{val}</span>
                      </div>
                    ))}
                  </div>

                  {/* Bluefin leg */}
                  <div className="p-3 space-y-1.5">
                    <p className="text-[10px] font-bold text-cyan-400 uppercase tracking-widest mb-2">Bluefin</p>
                    {[
                      ['Yön',       <span className={card.bfp.side === 'long' ? 'text-emerald-400' : 'text-rose-400'}>{card.bfp.side?.toUpperCase()}</span>],
                      ['Miktar',    `${Math.abs(card.bfp.positionQty || 0).toFixed(4)} ${card.base}`],
                      ['Giriş',     `$${fmt(card.bfp.avgEntryPrice || 0)}`],
                      ['Mark',      `$${fmt(card.bfp.markPrice || 0)}`],
                      ['uPnL',      <span className={pnlColor(card.bfUPnl)}>{fmtPnl(card.bfUPnl)}</span>],
                    ].map(([label, val]) => (
                      <div key={label as string} className="flex items-center justify-between text-[11px]">
                        <span className="text-blue-500">{label}</span>
                        <span className="font-mono text-white">{val}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer: funding + all-in PnL + close button */}
                <div className="border-t border-[#0a2560] px-4 py-2.5 bg-[#061530]/60">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 grid grid-cols-2 gap-3 text-[11px]">
                      <div>
                        <p className="text-blue-500 mb-0.5">Net Funding</p>
                        <p className={`font-mono ${pnlColor(card.dydxFunding + card.bfFundingNet)}`}>
                          {fmtPnl(card.dydxFunding + card.bfFundingNet)}
                          <span className="text-blue-600 text-[9px] ml-1">(dYdX {fmtPnl(card.dydxFunding)} + BF {fmtPnl(card.bfFundingNet)})</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-blue-500 mb-0.5">All-in PnL</p>
                        <p className={`font-mono font-bold text-sm ${pnlColor(card.netAllIn)}`}>
                          {fmtPnl(card.netAllIn)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setCloseCard({
                        base:      card.base,
                        dydxTkr:  card.dydxTkr,
                        bfSymbol: card.bfp.symbol,
                        dydxSide: card.dydxP.side,
                        bfSide:   card.bfp.side,
                        dydxSize: parseFloat(card.dydxP.size),
                        bfSize:   Math.abs(card.bfp.positionQty || 0),
                        matchRow: matchRow || null,
                      })}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-300 text-xs font-semibold hover:bg-rose-500/20 transition-colors"
                    >
                      <X size={12} /> Kapat
                    </button>
                  </div>
                </div>
              </div>
            );
            });

          })()}
        </div>
      )}
    </div>
  );
}
